// O card do passo na PÁGINA da viagem (#6313): a anatomia dos cards da H6 — thumb
// de 44, título `ui`, linhas `caption` em muted, valor e selo no polo direito. O
// drawer da conversa (`timeline`) e o console (`cards`) seguem com o card de sempre
// (`stop_row.tsx`); só o que muda de CONTEÚDO na página mora aqui.
import { CheckCircleIcon, ClockIcon, SignInIcon } from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { AirlineMark, hasAirlineMark } from './airline_logos.js';
import { cityLabel, splitAirport, voucherStatusPill, type TripStop } from './derive.js';
import { t } from './i18n.js';
import { cabinLabel, dayHeading, stopDetail, stopsLabel, stopTitle } from './labels.js';
import { ModalIcon } from './primitives.js';

/** Uma linha do miolo: o texto e, opcional, o glifo que a abre (o SignIn do check-out). */
export interface PageCardLine {
  text: string;
  icon?: 'checkOut';
}

/** O título e as linhas do card da página. O endereço do hotel é linha à parte porque
 *  ele é COPIÁVEL (sublinhado + Copy), não só texto. */
export interface PageCardView {
  title: string;
  address: string | null;
  lines: PageCardLine[];
}

/** «GIG · Rio de Janeiro» quando a fonte nomeia a cidade; senão só o código. */
function airportLabel(raw: string): string {
  const { code } = splitAirport(raw);
  const city = cityLabel(raw);
  return city ? `${code} · ${city}` : code;
}

/** A hora do check-out do hotel, «Check-out Sáb 29 · 11:00»: o dia e, quando a fonte
 *  manda, a hora. */
function checkOutLine(checkOut: Date | null, hour: string | null): string | null {
  if (!checkOut) return null;
  return [`${t('trips.agenda.checkOut')} ${dayHeading(checkOut)}`, hour]
    .filter(Boolean)
    .join(' · ');
}

/** O conteúdo do card por modal, na anatomia da página: o voo pela rota com as cidades e
 *  a linha «LA 1234 · Direto · Econômica · chega 13:40» (a hora de saída subiu para o
 *  rótulo do passo); o hotel pelo nome, o endereço copiável, as noites e o check-out; os
 *  demais modais no vocabulário de sempre. A diária e a foto do hotel não entram: a fonte
 *  não manda tarifa por noite nem fachada. */
export function pageCardView(stop: TripStop): PageCardView {
  const leg = stop.leg;
  if (stop.service.modal === 'FLIGHT' && leg?.from && leg.to) {
    return flightView(stop, { ...leg, from: leg.from, to: leg.to });
  }
  const hotel = stop.service.detail;
  if (hotel?.kind === 'HOTEL') return hotelView(stop, hotel);
  const { name, flightNo } = stopTitle(stop);
  const detail = stopDetail(stop);
  return {
    title: [name, flightNo].filter(Boolean).join(' · '),
    address: null,
    lines: detail ? [{ text: detail }] : [],
  };
}

type FlightLeg = NonNullable<TripStop['leg']> & { from: string; to: string };
type HotelDetail = Extract<NonNullable<TripStop['service']['detail']>, { kind: 'HOTEL' }>;

function flightView(stop: TripStop, leg: FlightLeg): PageCardView {
  const { flightNo } = stopTitle(stop);
  const arrives = leg.arriveTime
    ? t('trips.panel.arrivesAt', { time: `${leg.arriveTime}${leg.arriveNextDay ? ' +1' : ''}` })
    : null;
  const text = [flightNo, stopsLabel(leg.stops), cabinLabel(leg.travelClass), arrives]
    .filter(Boolean)
    .join(' · ');
  return {
    title: `${airportLabel(leg.from)} → ${airportLabel(leg.to)}`,
    address: null,
    lines: text ? [{ text }] : [],
  };
}

function hotelView(stop: TripStop, hotel: HotelDetail): PageCardView {
  const nights = hotel.nights == null ? null : t('trips.agenda.nights', { count: hotel.nights });
  const checkOut = checkOutLine(hotel.checkOut, hotel.checkOutHour);
  const lines: PageCardLine[] = [];
  if (nights) lines.push({ text: nights });
  if (checkOut) lines.push({ text: checkOut, icon: 'checkOut' });
  return {
    title: stopTitle(stop).name,
    address: [hotel.address, hotel.city].filter(Boolean).join(', ') || null,
    lines,
  };
}

/** Uma linha do miolo, com o glifo quando ela tem um. */
export function PageCardLineView({ line }: Readonly<{ line: PageCardLine }>) {
  return (
    <span className="tp-stop__sub">
      {line.icon === 'checkOut' && <SignInIcon size={14} aria-hidden />}
      {line.text}
    </span>
  );
}

/** O thumb de 44: a logo da companhia em 24 no voo; os demais modais (e a companhia sem
 *  logo curada) no glifo do modal sobre o neutro de apoio. */
export function PageThumb({ stop }: Readonly<{ stop: TripStop }>) {
  const carrier = stop.leg?.carrier;
  if (stop.service.modal === 'FLIGHT' && hasAirlineMark(carrier)) {
    return (
      // Sem `aria-hidden`: a marca é ROTULADA com o nome da companhia, e é ela quem diz
      // ao leitor de tela quem opera o voo (o título é só a rota).
      <span className="tp-stop__thumb tp-stop__thumb--mark">
        <AirlineMark carrier={carrier} className="tp-stop__logo" size={24} />
      </span>
    );
  }
  return (
    <span className="tp-stop__thumb" aria-hidden="true">
      <ModalIcon modal={stop.service.modal} size={20} />
    </span>
  );
}

/** «Aguardando Marina» — quem decide o voucher em aprovação, pelos nomes que a fonte
 *  manda (`pendingApprovers`). Sem nome, o rótulo de sempre do estado. */
function awaitingLabel(service: TripStop['service']): string {
  const names = (service.voucher.pendingApprovers ?? []).map((approver) => approver.name);
  if (names.length === 0) return t('trips.voucherStatus.AWAITING_APPROVAL');
  return t('trips.panel.awaitingApprover', { name: names.join(', ') });
}

/** O selo da página: «Emitido» é o Badge ghost neutro (check + texto muted, sem fundo);
 *  em aprovação, o Badge soft warning com quem decide; as demais esperas em texto. */
export function PageStopState({ service }: Readonly<{ service: TripStop['service'] }>) {
  if (service.status === 'AWAITING_APPROVAL') {
    return (
      <Badge variant="soft" color="warning">
        <ClockIcon aria-hidden />
        {awaitingLabel(service)}
      </Badge>
    );
  }
  const pill = voucherStatusPill(service.status, service.voucher);
  if (!pill) return null;
  if (pill.variant === 'confirmed') {
    return (
      <Badge variant="ghost" className="px-0 text-muted-foreground hover:bg-transparent">
        <CheckCircleIcon aria-hidden />
        {t(pill.labelKey)}
      </Badge>
    );
  }
  return (
    <span className="tp-stop__st">
      <ClockIcon size={14} aria-hidden />
      {t(pill.labelKey)}
    </span>
  );
}
